import React, { useCallback, useEffect, useState } from 'react';

import { errorMessage, formatAmount, formatCount, IN_PROGRESS_STATUSES, POLL_INTERVAL_MS, statusBadge } from './uploadFormat';

import { formatDateWithTz } from 'Helpers/dateUtils';
import { apiClient } from 'Helpers/network';

import Alert from 'CommonComponents/Alert';
import Badge from 'CommonComponents/Badge';
import Button from 'CommonComponents/Button';
import EmptyState from 'CommonComponents/EmptyState';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

import ClockIcon from 'Assets/icons/clock.svg';

const PER_PAGE = 20;
const ISO_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
const COLUMN_COUNT = 8;

/**
 * Every upload attempt for the project — blocked, failed and loaded alike.
 */
export default function UploadHistoryTab({ historyUrl, uploadUrl }) {
    const [page, setPage] = useState(1);
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(
        (quiet = false) => {
            if (!quiet) {
                setLoading(true);
            }

            return apiClient
                .get(historyUrl, { params: { page, per_page: PER_PAGE } })
                .then((response) => {
                    setHistory(response.data);
                    setError(null);
                })
                .catch((requestError) => setError(errorMessage(requestError)))
                .finally(() => setLoading(false));
        },
        [historyUrl, page],
    );

    useEffect(() => {
        load();
    }, [load]);

    const rows = history?.data ?? [];
    const anyInProgress = rows.some((row) => IN_PROGRESS_STATUSES.includes(row.status));

    // Keeps a queued write moving on screen without a reload.
    useEffect(() => {
        if (!anyInProgress) {
            return undefined;
        }

        const timer = window.setInterval(() => load(true), POLL_INTERVAL_MS);

        return () => window.clearInterval(timer);
    }, [anyInProgress, load]);

    const meta = history?.meta;
    // The issue report download needs the create permission; a read-only user
    // still sees the history.
    const canCreate = history?.can_create === true;

    return (
        <section className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5">
            <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
                <div>
                    <h3 className="table__header">{$t('Upload history')}</h3>
                    <p className="tw-mt-1 tw-text-xs tw-text-charcoal-50">{$t('Every upload attempt since this history started, including blocked ones.')}</p>
                </div>
                {meta && meta.total > 0 && (
                    <div className="table__pagination-info">
                        <PaginationInfo from={meta.from} to={meta.to} total={meta.total} />
                        <TablePagination lastPage={meta.last_page} currentPage={page} pageChange={(data) => setPage(+data.selected + 1)} />
                    </div>
                )}
            </div>

            {!!error && (
                <div className="tw-mt-4">
                    <Alert type="error">{error}</Alert>
                </div>
            )}

            {!loading && rows.length === 0 && !error ? (
                <EmptyState
                    className="tw-py-10"
                    icon={ClockIcon}
                    heading={$t('No uploads yet')}
                    body={$t('Uploads of actuals from Excel appear here, including any that were blocked.')}
                    action={
                        uploadUrl && (
                            <Button as="a" small variant="primary" href={uploadUrl}>
                                {$t('Import Actuals from Excel')}
                            </Button>
                        )
                    }
                />
            ) : (
                <div className="inbud-table_wrapper tw-mt-4">
                    <table className="inbud-table tw-w-full">
                        <thead>
                            <tr>
                                <th>{$t('Reference')}</th>
                                <th>{$t('Uploaded')}</th>
                                <th>{$t('User')}</th>
                                <th>{$t('File')}</th>
                                <th>{$t('Status')}</th>
                                <th className="tw-text-end">{$t('Rows written')}</th>
                                <th className="tw-text-end">{$t('Value')}</th>
                                <th className="tw-text-end">{$t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <TableRowSkeleton rows={5} columns={COLUMN_COUNT} />}
                            {!loading &&
                                rows.map((row) => {
                                    const badge = statusBadge(row.status);

                                    return (
                                        <tr key={row.batch_id}>
                                            <td className="tw-font-medium">{row.reference}</td>
                                            <td className="inbud-date-cell">{formatDateWithTz(row.created_at, ISO_FORMAT, 'DD/MM/YYYY HH:mm')}</td>
                                            <td>{row.user || '-'}</td>
                                            <td className="tw-max-w-xs tw-truncate" title={row.file_name}>
                                                {row.file_name}
                                            </td>
                                            <td>
                                                <Badge variant={badge.variant}>{badge.label}</Badge>
                                                {!!row.error_message && <div className="tw-mt-1 tw-text-xs tw-text-red">{row.error_message}</div>}
                                            </td>
                                            <td className="tw-text-end">{formatCount(row.rows_written)}</td>
                                            <td className="tw-text-end">{row.value === null ? '–' : formatAmount(row.value)}</td>
                                            <td className="tw-text-end">
                                                <div className="tw-flex tw-items-center tw-justify-end tw-gap-x-2">
                                                    {canCreate && row.rows_read > 0 && (
                                                        <Button as="a" small variant="secondary" href={row.issues_url}>
                                                            {$t('Issue report')}
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}
